// src/components/maps/admin/WayfindingGraphEditor.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, ImageOverlay, CircleMarker, Polyline, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AddCircle as AddNodeIcon,
  AddLink as AddEdgeIcon,
  PanTool as SelectIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Link as LinkIcon,
  Room as RoomIcon,
  Search as SearchIcon,
  DoorFront as EntranceIcon,
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
  Layers as LayersIcon,
  GpsFixed as CalibrateIcon,
} from '@mui/icons-material';
import indoorMappingService from '../../../services/indoorMappingService';
import { locationsApi } from '../../../services/apiClient';
import NodeCalibrationMode from './NodeCalibrationMode';

/**
 * Rotatable image overlay for floor plans with rotation
 * Uses CSS transform to rotate the image
 */
const RotatableFloorPlanOverlay = ({ url, bounds, rotation }) => {
  const map = useMap();
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Create container and image elements
  useEffect(() => {
    if (!map || !url) return;

    // Create a custom pane for the floor plan
    if (!map.getPane('floorPlanPane')) {
      const pane = map.createPane('floorPlanPane');
      pane.style.zIndex = 250; // Below markers (400) but above tiles (200)
    }

    // Create container
    const container = document.createElement('div');
    container.className = 'floor-plan-rotatable';
    container.style.position = 'absolute';
    container.style.pointerEvents = 'none';
    containerRef.current = container;
    map.getPane('floorPlanPane').appendChild(container);

    // Create image
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.style.display = 'block';
    img.style.width = '100%';
    img.style.height = '100%';
    img.onload = () => setImageLoaded(true);
    img.src = url;
    imgRef.current = img;
    container.appendChild(img);

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      containerRef.current = null;
      imgRef.current = null;
      setImageLoaded(false);
    };
  }, [map, url]);

  // Update position and rotation when map moves or bounds change
  useEffect(() => {
    if (!map || !containerRef.current || !bounds || !imageLoaded) return;

    const updatePosition = () => {
      const container = containerRef.current;
      if (!container) return;

      // Get pixel positions for bounds corners
      const sw = map.latLngToLayerPoint([bounds[0][0], bounds[0][1]]);
      const ne = map.latLngToLayerPoint([bounds[1][0], bounds[1][1]]);

      const width = ne.x - sw.x;
      const height = sw.y - ne.y;

      // Position at center, then translate to top-left
      const centerX = (sw.x + ne.x) / 2;
      const centerY = (sw.y + ne.y) / 2;

      container.style.width = `${Math.abs(width)}px`;
      container.style.height = `${Math.abs(height)}px`;
      container.style.left = `${centerX - Math.abs(width) / 2}px`;
      container.style.top = `${centerY - Math.abs(height) / 2}px`;
      container.style.transform = `rotate(${rotation}deg)`;
      container.style.transformOrigin = 'center center';
    };

    updatePosition();
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);

    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [map, bounds, rotation, imageLoaded]);

  return null;
};

/**
 * Map click handler component
 */
const MapClickHandler = ({ mode, nodeType, onAddNode, onSelectNode }) => {
  useMapEvents({
    click: (e) => {
      if (mode === 'addNode') {
        onAddNode(e.latlng.lat, e.latlng.lng, nodeType);
      } else if (mode === 'select') {
        // Node selection is handled by CircleMarker click events
      }
    },
  });
  return null;
};

/**
 * Wayfinding graph editor for creating navigation nodes and edges
 */
const WayfindingGraphEditor = ({ floorPlan }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [mode, setMode] = useState('select'); // 'select', 'addNode', 'addEdge', 'delete'
  const [nodeType, setNodeType] = useState('normal'); // 'normal', 'stairs', 'elevator', 'entrance'
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Location linking state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [linkedLocations, setLinkedLocations] = useState({}); // nodeId -> location mapping
  const [locationSearch, setLocationSearch] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Entrance creation dialog state
  const [entranceDialogOpen, setEntranceDialogOpen] = useState(false);
  const [pendingEntranceNode, setPendingEntranceNode] = useState(null); // Node waiting for entrance details
  const [entranceForm, setEntranceForm] = useState({
    name: '',
    entrance_type: 'main',
    is_default: false,
    is_accessible: true,
  });

  // Node editing state
  const [editNodeDialogOpen, setEditNodeDialogOpen] = useState(false);
  const [editNodeForm, setEditNodeForm] = useState({
    name: '',
    geo_lat: '',
    geo_lng: '',
    node_type: 'normal',
  });

  // Multi-floor connection state
  const [multiFloorDialogOpen, setMultiFloorDialogOpen] = useState(false);
  const [otherFloorPlans, setOtherFloorPlans] = useState([]);
  const [selectedTargetFloor, setSelectedTargetFloor] = useState(null);
  const [targetFloorNodes, setTargetFloorNodes] = useState([]);
  const [selectedTargetNode, setSelectedTargetNode] = useState(null);
  const [multiFloorEdgeType, setMultiFloorEdgeType] = useState('stairs_up');
  const [loadingTargetFloor, setLoadingTargetFloor] = useState(false);

  // Edge type for normal edges
  const [edgeType, setEdgeType] = useState('corridor');

  // Multi-floor connections for selected node
  const [multiFloorConnections, setMultiFloorConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  // Node calibration mode state
  const [calibrationModeOpen, setCalibrationModeOpen] = useState(false);

  // Building entrances state (for linking entrance nodes)
  const [buildingEntrances, setBuildingEntrances] = useState([]);
  const [linkEntranceDialogOpen, setLinkEntranceDialogOpen] = useState(false);
  const [pendingLinkEntranceNode, setPendingLinkEntranceNode] = useState(null);

  // Load existing graph
  useEffect(() => {
    if (floorPlan?.id) {
      loadGraph();
    }
  }, [floorPlan]);

  const loadGraph = async () => {
    try {
      setLoading(true);
      const graph = await indoorMappingService.getWayfindingGraph(floorPlan.id);
      setNodes(graph.nodes || []);
      setEdges(graph.edges || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load wayfinding graph:', err);
      showSnackbar('Failed to load graph', 'error');
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Load locations that can be linked (rooms, units, etc.)
  const loadLocationsForLinking = async () => {
    setLoadingLocations(true);
    try {
      const response = await locationsApi.getAll({ limit: 500 });
      const locations = response.data?.data || response.data || [];

      // Filter to room-level locations that don't already have a linked node
      // Or show all and indicate which ones are already linked
      setAvailableLocations(locations);

      // Build a map of which nodes are linked to which locations
      const linked = {};
      locations.forEach(loc => {
        if (loc.wayfinding_node_id) {
          linked[loc.wayfinding_node_id] = loc;
        }
      });
      setLinkedLocations(linked);
    } catch (err) {
      console.error('Failed to load locations:', err);
      showSnackbar('Failed to load locations', 'error');
    } finally {
      setLoadingLocations(false);
    }
  };

  // Handle opening link location dialog
  const handleOpenLinkDialog = async () => {
    if (selectedNodes.length !== 1) {
      showSnackbar('Please select exactly one node to link', 'warning');
      return;
    }
    setLinkDialogOpen(true);
    await loadLocationsForLinking();
  };

  // Handle linking a location to the selected node
  const handleLinkLocation = async (location) => {
    if (selectedNodes.length !== 1) return;

    const nodeId = selectedNodes[0].id;

    try {
      // Update the location's wayfinding_node_id
      await locationsApi.update(location.id, {
        wayfinding_node_id: nodeId
      });

      // Update local state
      setLinkedLocations(prev => ({
        ...prev,
        [nodeId]: location
      }));

      showSnackbar(`Linked "${location.friendlyName}" to this node`, 'success');
      setLinkDialogOpen(false);
    } catch (err) {
      console.error('Failed to link location:', err);
      showSnackbar(err.message || 'Failed to link location', 'error');
    }
  };

  // Load building entrances for the location
  const loadBuildingEntrances = async () => {
    if (!floorPlan?.location_id) return;
    try {
      const entrances = await indoorMappingService.getEntrances(floorPlan.location_id);
      setBuildingEntrances(entrances || []);
    } catch (err) {
      console.error('Failed to load building entrances:', err);
    }
  };

  // Load entrances when floor plan changes
  useEffect(() => {
    if (floorPlan?.location_id) {
      loadBuildingEntrances();
    }
  }, [floorPlan?.location_id]);

  // Handle opening link entrance dialog for an entrance node
  const handleOpenLinkEntranceDialog = (node) => {
    if (node.node_type !== 'entrance') {
      showSnackbar('Only entrance nodes can be linked to building entrances', 'warning');
      return;
    }
    setPendingLinkEntranceNode(node);
    setLinkEntranceDialogOpen(true);
  };

  // Handle linking a building entrance to a wayfinding node
  const handleLinkBuildingEntrance = async (entrance) => {
    if (!pendingLinkEntranceNode) return;

    try {
      await indoorMappingService.updateEntrance(entrance.id, {
        wayfinding_node_id: pendingLinkEntranceNode.id
      });

      showSnackbar(`Linked "${entrance.name}" to wayfinding node`, 'success');
      setLinkEntranceDialogOpen(false);
      setPendingLinkEntranceNode(null);
      // Reload entrances to update state
      await loadBuildingEntrances();
    } catch (err) {
      console.error('Failed to link entrance:', err);
      showSnackbar(err.message || 'Failed to link entrance', 'error');
    }
  };

  // Check if an entrance node is linked to a building entrance
  const getLinkedEntrance = (nodeId) => {
    return buildingEntrances.find(e => e.wayfinding_node_id === nodeId);
  };

  // Handle unlinking a location from a node
  const handleUnlinkLocation = async (nodeId) => {
    const location = linkedLocations[nodeId];
    if (!location) return;

    try {
      await locationsApi.update(location.id, {
        wayfinding_node_id: null
      });

      setLinkedLocations(prev => {
        const updated = { ...prev };
        delete updated[nodeId];
        return updated;
      });

      showSnackbar(`Unlinked "${location.friendlyName}" from this node`, 'success');
    } catch (err) {
      console.error('Failed to unlink location:', err);
      showSnackbar(err.message || 'Failed to unlink location', 'error');
    }
  };

  // Filter locations based on search
  const filteredLocations = availableLocations.filter(loc =>
    loc.friendlyName?.toLowerCase().includes(locationSearch.toLowerCase()) ||
    loc.locationTypeModel?.friendlyName?.toLowerCase().includes(locationSearch.toLowerCase())
  );

  // Load multi-floor connections for a node
  const loadMultiFloorConnections = async (nodeId) => {
    if (!nodeId || !floorPlan?.location_id) return;

    setLoadingConnections(true);
    try {
      // Get all edges for this node that connect to other floors
      const connections = await indoorMappingService.getNodeMultiFloorConnections(nodeId);
      setMultiFloorConnections(connections || []);
    } catch (err) {
      console.error('Failed to load multi-floor connections:', err);
      // If the API doesn't exist yet, try to derive from local edges
      const nodeConnections = edges.filter(e => {
        const fromId = e.from_node_id ?? e.nodeAId;
        const toId = e.to_node_id ?? e.nodeBId;
        const edgeType = e.edge_type || e.type || '';
        const isMultiFloor = edgeType.includes('stairs') || edgeType.includes('elevator');
        return isMultiFloor && (fromId === nodeId || toId === nodeId);
      });
      setMultiFloorConnections(nodeConnections);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Load connections when a stairs/elevator node is selected
  useEffect(() => {
    if (selectedNodes.length === 1) {
      const node = selectedNodes[0];
      const type = node.node_type || node.type;
      if (type === 'stairs' || type === 'elevator') {
        loadMultiFloorConnections(node.id);
      } else {
        setMultiFloorConnections([]);
      }
    } else {
      setMultiFloorConnections([]);
    }
  }, [selectedNodes]);

  // Load other floor plans for multi-floor connections
  const loadOtherFloorPlans = async () => {
    if (!floorPlan?.location_id) return;
    try {
      const floorPlans = await indoorMappingService.getFloorPlans(floorPlan.location_id);
      // Filter out current floor plan
      const others = floorPlans.filter(fp => fp.id !== floorPlan.id);
      setOtherFloorPlans(others);
    } catch (err) {
      console.error('Failed to load floor plans:', err);
      showSnackbar('Failed to load other floors', 'error');
    }
  };

  // Load all floor plans with their nodes for calibration
  const loadAllFloorsWithNodes = async () => {
    if (!floorPlan?.location_id) return [];
    try {
      setLoading(true);
      const floorPlans = await indoorMappingService.getFloorPlans(floorPlan.location_id);

      // Load nodes for each floor plan
      const floorsWithNodes = await Promise.all(
        floorPlans.map(async (fp) => {
          try {
            const graph = await indoorMappingService.getWayfindingGraph(fp.id);
            return {
              ...fp,
              nodes: graph.nodes || [],
              edges: graph.edges || [],
            };
          } catch (err) {
            console.error(`Failed to load nodes for floor ${fp.id}:`, err);
            return { ...fp, nodes: [], edges: [] };
          }
        })
      );

      // Update otherFloorPlans with nodes (for multi-floor connections too)
      const others = floorsWithNodes.filter(fp => fp.id !== floorPlan.id);
      setOtherFloorPlans(others);

      return floorsWithNodes;
    } catch (err) {
      console.error('Failed to load floor plans with nodes:', err);
      showSnackbar('Failed to load floor data', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // State to hold all floors with nodes for calibration
  const [allFloorsWithNodes, setAllFloorsWithNodes] = useState([]);

  // Open calibration mode with all data loaded
  const handleOpenCalibrationMode = async () => {
    const floors = await loadAllFloorsWithNodes();
    if (floors.length > 0) {
      setAllFloorsWithNodes(floors);
      setCalibrationModeOpen(true);
    }
  };

  // Load nodes from target floor
  const loadTargetFloorNodes = async (targetFloorPlan) => {
    setLoadingTargetFloor(true);
    try {
      const graph = await indoorMappingService.getWayfindingGraph(targetFloorPlan.id);
      setTargetFloorNodes(graph.nodes || []);
      setSelectedTargetFloor(targetFloorPlan);
    } catch (err) {
      console.error('Failed to load target floor nodes:', err);
      showSnackbar('Failed to load nodes from target floor', 'error');
    } finally {
      setLoadingTargetFloor(false);
    }
  };

  // Open multi-floor connection dialog
  const handleOpenMultiFloorDialog = async () => {
    if (selectedNodes.length !== 1) {
      showSnackbar('Please select a stairs or elevator node first', 'warning');
      return;
    }
    const node = selectedNodes[0];
    const nodeType = node.node_type || node.type;
    if (nodeType !== 'stairs' && nodeType !== 'elevator') {
      showSnackbar('Multi-floor connections require a stairs or elevator node', 'warning');
      return;
    }

    // Set default edge type based on node type
    setMultiFloorEdgeType(nodeType === 'elevator' ? 'elevator_up' : 'stairs_up');
    setSelectedTargetFloor(null);
    setTargetFloorNodes([]);
    setSelectedTargetNode(null);
    setMultiFloorDialogOpen(true);
    await loadOtherFloorPlans();
  };

  // Get the reverse edge type for bidirectional multi-floor connections
  const getReverseEdgeType = (edgeType) => {
    const reverseMap = {
      stairs_up: 'stairs_down',
      stairs_down: 'stairs_up',
      elevator_up: 'elevator_down',
      elevator_down: 'elevator_up',
    };
    return reverseMap[edgeType] || edgeType;
  };

  // Create multi-floor edge (automatically creates reverse edge for bidirectional travel)
  const handleCreateMultiFloorEdge = async () => {
    if (!selectedNodes[0] || !selectedTargetNode) {
      showSnackbar('Please select both source and target nodes', 'warning');
      return;
    }

    try {
      // Create the primary edge (e.g., stairs_up from Floor 1 to Floor 2)
      const edge = await indoorMappingService.createWayfindingEdge({
        from_node_id: selectedNodes[0].id,
        to_node_id: selectedTargetNode.id,
        edge_type: multiFloorEdgeType,
        is_bidirectional: false,
      });

      // Automatically create the reverse edge (e.g., stairs_down from Floor 2 to Floor 1)
      const reverseEdgeType = getReverseEdgeType(multiFloorEdgeType);
      const reverseEdge = await indoorMappingService.createWayfindingEdge({
        from_node_id: selectedTargetNode.id,
        to_node_id: selectedNodes[0].id,
        edge_type: reverseEdgeType,
        is_bidirectional: false,
      });

      // Link the nodes together for floor transition tracking
      await indoorMappingService.updateWayfindingNode(selectedNodes[0].id, {
        linked_node_id: selectedTargetNode.id
      });
      await indoorMappingService.updateWayfindingNode(selectedTargetNode.id, {
        linked_node_id: selectedNodes[0].id
      });

      setEdges([...edges, edge, reverseEdge]);

      // Refresh the multi-floor connections list to show the new connection
      if (selectedNodes[0]) {
        loadMultiFloorConnections(selectedNodes[0].id);
      }

      showSnackbar(`Multi-floor connection created (both directions)`, 'success');
      setMultiFloorDialogOpen(false);
      setSelectedTargetNode(null);
    } catch (err) {
      showSnackbar(err.message || 'Failed to create multi-floor connection', 'error');
    }
  };

  const handleAddNode = async (lat, lng, type) => {
    try {
      // Convert geo coordinates to pixel coordinates inline
      // (geoToPixel is defined after boundsData, so we calculate here)
      let x = 0, y = 0;
      const bounds = getBounds();
      if (bounds && floorPlan?.image_width && floorPlan?.image_height) {
        const xRatio = (lng - bounds.west) / (bounds.east - bounds.west);
        const yRatio = (bounds.north - lat) / (bounds.north - bounds.south);
        x = Math.round(xRatio * floorPlan.image_width);
        y = Math.round(yRatio * floorPlan.image_height);
      }

      // API expects: floor_plan_id, node_type, x_position, y_position, geo_lat, geo_lng
      // x_position/y_position are PIXEL coordinates (INTEGER in database)
      // geo_lat/geo_lng are geographic coordinates (DECIMAL in database)
      const node = await indoorMappingService.createWayfindingNode({
        floor_plan_id: floorPlan.id,
        node_type: type,
        x_position: x,  // Pixel X coordinate on floor plan image
        y_position: y,  // Pixel Y coordinate on floor plan image
        geo_lat: lat,   // Geographic latitude
        geo_lng: lng,   // Geographic longitude
      });

      setNodes([...nodes, node]);
      showSnackbar('Node added successfully', 'success');

      // If this is an entrance node, prompt to create a BuildingEntrance record
      if (type === 'entrance') {
        setPendingEntranceNode(node);
        setEntranceForm({
          name: '',
          entrance_type: 'main',
          is_default: false,
          is_accessible: true,
        });
        setEntranceDialogOpen(true);
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to add node', 'error');
    }
  };

  // Handle creating building entrance after entrance node is created
  const handleCreateBuildingEntrance = async () => {
    if (!pendingEntranceNode || !entranceForm.name) {
      showSnackbar('Please enter a name for the entrance', 'warning');
      return;
    }

    try {
      // Get location_id from the floor plan
      const locationId = floorPlan.location_id;
      if (!locationId) {
        showSnackbar('Floor plan has no associated location', 'error');
        setEntranceDialogOpen(false);
        return;
      }

      // Use the pixel coordinates from the node (already converted when node was created)
      const entranceData = {
        location_id: locationId,
        floor_plan_id: floorPlan.id,
        name: entranceForm.name,
        entrance_type: entranceForm.entrance_type,
        x_position: pendingEntranceNode.x_position, // Pixel X from node
        y_position: pendingEntranceNode.y_position, // Pixel Y from node
        wayfinding_node_id: pendingEntranceNode.id,
        geo_lat: pendingEntranceNode.geo_lat,
        geo_lng: pendingEntranceNode.geo_lng,
        is_default: entranceForm.is_default,
        is_accessible: entranceForm.is_accessible,
      };

      await indoorMappingService.createEntrance(entranceData);
      showSnackbar('Building entrance created and linked to node', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to create building entrance', 'error');
    }

    setEntranceDialogOpen(false);
    setPendingEntranceNode(null);
  };

  // Skip creating building entrance but keep the node
  const handleSkipBuildingEntrance = () => {
    showSnackbar('Entrance node created without BuildingEntrance record', 'info');
    setEntranceDialogOpen(false);
    setPendingEntranceNode(null);
  };

  // Open edit node dialog
  const handleOpenEditNode = () => {
    if (selectedNodes.length !== 1) return;
    const node = selectedNodes[0];
    setEditNodeForm({
      name: node.name || '',
      geo_lat: node.geo_lat?.toString() || node.y_position?.toString() || '',
      geo_lng: node.geo_lng?.toString() || node.x_position?.toString() || '',
      node_type: node.node_type || node.type || 'normal',
    });
    setEditNodeDialogOpen(true);
  };

  // Save node edits
  const handleSaveNodeEdit = async () => {
    if (selectedNodes.length !== 1) return;
    const node = selectedNodes[0];

    try {
      const lat = parseFloat(editNodeForm.geo_lat);
      const lng = parseFloat(editNodeForm.geo_lng);

      if (isNaN(lat) || isNaN(lng)) {
        showSnackbar('Please enter valid coordinates', 'error');
        return;
      }

      // Convert geo coordinates to pixel coordinates inline
      let x = 0, y = 0;
      const bounds = getBounds();
      if (bounds && floorPlan?.image_width && floorPlan?.image_height) {
        const xRatio = (lng - bounds.west) / (bounds.east - bounds.west);
        const yRatio = (bounds.north - lat) / (bounds.north - bounds.south);
        x = Math.round(xRatio * floorPlan.image_width);
        y = Math.round(yRatio * floorPlan.image_height);
      }

      const updatedNode = await indoorMappingService.updateWayfindingNode(node.id, {
        name: editNodeForm.name || null,
        geo_lat: lat,
        geo_lng: lng,
        x_position: x,  // Pixel X coordinate
        y_position: y,  // Pixel Y coordinate
        node_type: editNodeForm.node_type,
      });

      // Update local state
      setNodes(nodes.map((n) => (n.id === node.id ? { ...n, ...updatedNode } : n)));
      setSelectedNodes([{ ...node, ...updatedNode }]);
      showSnackbar('Node updated successfully', 'success');
      setEditNodeDialogOpen(false);
    } catch (err) {
      showSnackbar(err.message || 'Failed to update node', 'error');
    }
  };

  const handleSelectNode = (node) => {
    if (mode === 'delete') {
      handleDeleteNode(node.id);
      return;
    }

    if (mode === 'addEdge') {
      // In edge mode, select two nodes to create edge
      if (selectedNodes.length === 0) {
        setSelectedNodes([node]);
      } else if (selectedNodes.length === 1) {
        if (selectedNodes[0].id !== node.id) {
          handleCreateEdge(selectedNodes[0].id, node.id);
        }
        setSelectedNodes([]);
      }
    } else {
      setSelectedNodes([node]);
    }
  };

  const handleCreateEdge = async (nodeAId, nodeBId) => {
    try {
      // API expects: from_node_id, to_node_id, edge_type, is_bidirectional
      const edge = await indoorMappingService.createWayfindingEdge({
        from_node_id: nodeAId,
        to_node_id: nodeBId,
        edge_type: edgeType,
        is_bidirectional: true,
      });

      setEdges([...edges, edge]);
      showSnackbar(`Edge created: ${edgeType}`, 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to create edge', 'error');
    }
  };

  const handleDeleteNode = async (nodeId) => {
    try {
      await indoorMappingService.deleteWayfindingNode(nodeId);
      setNodes(nodes.filter((n) => n.id !== nodeId));
      // Handle both API format (from_node_id/to_node_id) and local format (nodeAId/nodeBId)
      setEdges(edges.filter((e) => {
        const fromId = e.from_node_id ?? e.nodeAId;
        const toId = e.to_node_id ?? e.nodeBId;
        return fromId !== nodeId && toId !== nodeId;
      }));
      showSnackbar('Node deleted successfully', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete node', 'error');
    }
  };

  const handleDeleteEdge = async (edgeId) => {
    try {
      await indoorMappingService.deleteWayfindingEdge(edgeId);
      setEdges(edges.filter((e) => e.id !== edgeId));
      showSnackbar('Edge deleted successfully', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete edge', 'error');
    }
  };

  // Handle both snake_case (API) and camelCase field names for image URL
  const imageUrl = floorPlan?.image_url || floorPlan?.imageUrl;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
  const fullImageUrl = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`)
    : null;

  // Calculate bounds - handle both nested object and flat properties
  const getBounds = () => {
    // Check for nested bounds object
    if (floorPlan?.bounds?.north && floorPlan?.bounds?.south && floorPlan?.bounds?.east && floorPlan?.bounds?.west) {
      return {
        north: floorPlan.bounds.north,
        south: floorPlan.bounds.south,
        east: floorPlan.bounds.east,
        west: floorPlan.bounds.west,
      };
    }
    // Check for flat properties (snake_case from API)
    if (floorPlan?.bounds_north && floorPlan?.bounds_south && floorPlan?.bounds_east && floorPlan?.bounds_west) {
      return {
        north: parseFloat(floorPlan.bounds_north),
        south: parseFloat(floorPlan.bounds_south),
        east: parseFloat(floorPlan.bounds_east),
        west: parseFloat(floorPlan.bounds_west),
      };
    }
    return null;
  };

  const boundsData = getBounds();
  const bounds = boundsData
    ? [
        [boundsData.south, boundsData.west],
        [boundsData.north, boundsData.east],
      ]
    : null;

  // Get rotation from floor plan (in degrees)
  const floorPlanRotation = parseFloat(floorPlan?.rotation_degrees || 0) || 0;
  const hasRotation = Math.abs(floorPlanRotation) > 0.1;

  // Convert pixel coordinates to GPS using floor plan geo-reference
  // This is the correct way to display nodes - based on their pixel position on the floor plan
  const pixelToGps = (pixelX, pixelY) => {
    if (!boundsData || !floorPlan?.image_width || !floorPlan?.image_height) {
      return null;
    }

    const imageWidth = floorPlan.image_width;
    const imageHeight = floorPlan.image_height;

    // Calculate center of floor plan in GPS
    const centerLat = (boundsData.north + boundsData.south) / 2;
    const centerLng = (boundsData.east + boundsData.west) / 2;

    // Calculate meters per pixel
    const latToMeters = 111320;
    const lngToMeters = 111320 * Math.cos(centerLat * Math.PI / 180);

    const imageHeightMeters = (boundsData.north - boundsData.south) * latToMeters;
    const imageWidthMeters = (boundsData.east - boundsData.west) * lngToMeters;

    const metersPerPixelY = imageHeightMeters / imageHeight;
    const metersPerPixelX = imageWidthMeters / imageWidth;

    // Offset from image center in pixels
    const offsetX = pixelX - imageWidth / 2;
    const offsetY = pixelY - imageHeight / 2;

    // Apply rotation if present
    const rotRad = floorPlanRotation * Math.PI / 180;
    const cosR = Math.cos(rotRad);
    const sinR = Math.sin(rotRad);

    // Transform pixel offset to GPS offset
    // For Y-down image coordinates with rotation:
    // East offset = offsetX * cos(R) + offsetY * sin(R)
    // North offset = offsetX * sin(R) - offsetY * cos(R)  (note: -offsetY because Y is down in image)
    const eastMeters = (offsetX * cosR + offsetY * sinR) * metersPerPixelX;
    const northMeters = (offsetX * sinR - offsetY * cosR) * metersPerPixelY;

    return {
      lat: centerLat + northMeters / latToMeters,
      lng: centerLng + eastMeters / lngToMeters,
    };
  };

  // Get node position for display - prefer pixel-to-GPS conversion, fallback to stored geo coords
  const getNodeDisplayPosition = (node) => {
    // If we have pixel coordinates and valid bounds, convert pixel to GPS
    if (node.x_position !== undefined && node.y_position !== undefined && boundsData) {
      const converted = pixelToGps(parseFloat(node.x_position), parseFloat(node.y_position));
      if (converted) {
        return converted;
      }
    }
    // Fallback to stored GPS coordinates
    if (node.geo_lat !== undefined && node.geo_lng !== undefined) {
      return { lat: parseFloat(node.geo_lat), lng: parseFloat(node.geo_lng) };
    }
    return null;
  };

  // Default center: Vinktstraat 1, Deinze, Belgium
  const DEFAULT_CENTER = [50.9847, 3.5278];

  const mapCenter = bounds
    ? [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
    : DEFAULT_CENTER;

  // Node color based on type
  const getNodeColor = (type) => {
    switch (type) {
      case 'stairs':
        return '#9C27B0';
      case 'elevator':
        return '#009688';
      case 'entrance':
        return '#4CAF50';
      default:
        return '#2196F3';
    }
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Wayfinding Graph Editor
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create navigation nodes and connect them with edges to build the wayfinding graph.
        </Typography>

        {/* Mode Selector */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" gutterBottom display="block">
            Edit Mode
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, value) => value && setMode(value)}
            size="small"
          >
            <ToggleButton value="select">
              <SelectIcon sx={{ mr: 0.5 }} />
              Select
            </ToggleButton>
            <ToggleButton value="addNode">
              <AddNodeIcon sx={{ mr: 0.5 }} />
              Add Node
            </ToggleButton>
            <ToggleButton value="addEdge">
              <AddEdgeIcon sx={{ mr: 0.5 }} />
              Add Edge
            </ToggleButton>
            <ToggleButton value="delete">
              <DeleteIcon sx={{ mr: 0.5 }} />
              Delete
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Node Type Selector (shown in addNode mode) */}
        {mode === 'addNode' && (
          <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
            <InputLabel>Node Type</InputLabel>
            <Select value={nodeType} onChange={(e) => setNodeType(e.target.value)} label="Node Type">
              <MenuItem value="normal">Normal Waypoint</MenuItem>
              <MenuItem value="stairs">Stairs</MenuItem>
              <MenuItem value="elevator">Elevator</MenuItem>
              <MenuItem value="entrance">Entrance</MenuItem>
              <MenuItem value="door">Door</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Edge Type Selector (shown in addEdge mode) */}
        {mode === 'addEdge' && (
          <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
            <InputLabel>Edge Type</InputLabel>
            <Select value={edgeType} onChange={(e) => setEdgeType(e.target.value)} label="Edge Type">
              <MenuItem value="corridor">Corridor (walking path)</MenuItem>
              <MenuItem value="door">Through Door</MenuItem>
              <MenuItem value="ramp">Ramp</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {mode === 'select' && 'Click on nodes to select them'}
          {mode === 'addNode' && 'Click on the map to place a new node'}
          {mode === 'addEdge' && 'Click two nodes to create an edge between them'}
          {mode === 'delete' && 'Click on nodes or edges to delete them'}
        </Alert>

        {/* Stats */}
        <Typography variant="body2" color="text.secondary">
          Nodes: {nodes.length} | Edges: {edges.length}
        </Typography>

        {/* Calibration Mode Button */}
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading ? <CircularProgress size={20} /> : <CalibrateIcon />}
          onClick={handleOpenCalibrationMode}
          disabled={loading}
          sx={{ mt: 2 }}
          fullWidth
        >
          {loading ? 'Loading...' : 'GPS Calibration Mode'}
        </Button>

        {/* Selected Node Info & Actions */}
        {selectedNodes.length === 1 && mode === 'select' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Node: {selectedNodes[0].name || `${selectedNodes[0].node_type || selectedNodes[0].type} (ID: ${selectedNodes[0].id})`}
                </Typography>
                {selectedNodes[0].name && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Type: {selectedNodes[0].node_type || selectedNodes[0].type} | ID: {selectedNodes[0].id}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" component="div">
                  Lat: {selectedNodes[0].geo_lat != null ? parseFloat(selectedNodes[0].geo_lat).toFixed(6) : selectedNodes[0].y_position ?? 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Lng: {selectedNodes[0].geo_lng != null ? parseFloat(selectedNodes[0].geo_lng).toFixed(6) : selectedNodes[0].x_position ?? 'N/A'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenEditNode}
              >
                Edit
              </Button>
            </Box>

            <Divider sx={{ my: 1 }} />

            {linkedLocations[selectedNodes[0].id] ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  icon={<RoomIcon />}
                  label={linkedLocations[selectedNodes[0].id].friendlyName}
                  color="success"
                  onDelete={() => handleUnlinkLocation(selectedNodes[0].id)}
                />
                <Typography variant="caption" color="text.secondary">
                  Linked location
                </Typography>
              </Box>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={handleOpenLinkDialog}
                sx={{ mt: 1 }}
              >
                Link to Location
              </Button>
            )}

            {/* Building Entrance link section for entrance nodes */}
            {selectedNodes[0].node_type === 'entrance' && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <EntranceIcon fontSize="small" color="success" />
                  Building Entrance
                </Typography>

                {(() => {
                  const linkedEntrance = getLinkedEntrance(selectedNodes[0].id);
                  if (linkedEntrance) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<EntranceIcon />}
                          label={linkedEntrance.name}
                          color="success"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          ✓ Navigation enabled
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                        Not linked - navigation won't work
                      </Alert>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<LinkIcon />}
                        onClick={() => handleOpenLinkEntranceDialog(selectedNodes[0])}
                      >
                        Link to Building Entrance
                      </Button>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {/* Multi-floor connections section for stairs/elevator nodes */}
            {(selectedNodes[0].node_type === 'stairs' || selectedNodes[0].node_type === 'elevator') && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <LayersIcon fontSize="small" color="secondary" />
                  Floor Connections
                </Typography>

                {loadingConnections ? (
                  <CircularProgress size={20} />
                ) : multiFloorConnections.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                    {(() => {
                      // Deduplicate connections - group by target floor, show as bidirectional
                      const seenFloors = new Set();
                      const uniqueConnections = multiFloorConnections.filter(conn => {
                        const targetFloor = conn.target_floor_name || conn.target_node_id;
                        if (seenFloors.has(targetFloor)) return false;
                        seenFloors.add(targetFloor);
                        return true;
                      });

                      return uniqueConnections.map((conn, index) => {
                        const edgeType = conn.edge_type || conn.type || '';
                        const targetNodeId = conn.target_node_id ||
                          ((conn.from_node_id ?? conn.nodeAId) === selectedNodes[0].id
                            ? (conn.to_node_id ?? conn.nodeBId)
                            : (conn.from_node_id ?? conn.nodeAId));
                        const connectionType = edgeType.includes('elevator') ? 'Elevator' : 'Stairs';

                        return (
                          <Chip
                            key={conn.id || index}
                            icon={edgeType.includes('elevator') ? <ElevatorIcon /> : <StairsIcon />}
                            label={`↕ ${conn.target_floor_name || `Node ${targetNodeId}`}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onDelete={async () => {
                              try {
                                // Delete this edge
                                await indoorMappingService.deleteWayfindingEdge(conn.id);

                                // Also find and delete the reverse edge (opposite direction)
                                const reverseEdge = multiFloorConnections.find(c =>
                                  c.id !== conn.id &&
                                  (c.target_node_id === selectedNodes[0].id ||
                                   (c.from_node_id === targetNodeId && c.to_node_id === selectedNodes[0].id))
                                );
                                if (reverseEdge) {
                                  await indoorMappingService.deleteWayfindingEdge(reverseEdge.id);
                                }

                                // Refresh connections list
                                loadMultiFloorConnections(selectedNodes[0].id);
                                setEdges(prev => prev.filter(e => e.id !== conn.id && e.id !== reverseEdge?.id));
                                showSnackbar('Connection removed (both directions)', 'success');
                              } catch (err) {
                                showSnackbar('Failed to remove connection', 'error');
                              }
                            }}
                          />
                        );
                      });
                    })()}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    No floor connections yet
                  </Typography>
                )}

                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  startIcon={<LayersIcon />}
                  onClick={handleOpenMultiFloorDialog}
                >
                  Add Floor Connection
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Map */}
      <Box sx={{ height: 600, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {bounds && (
          <MapContainer
            center={mapCenter}
            zoom={18}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            {/* Floor plan overlay - use rotatable version when rotation is set */}
            {fullImageUrl && hasRotation && (
              <RotatableFloorPlanOverlay
                url={fullImageUrl}
                bounds={bounds}
                rotation={floorPlanRotation}
              />
            )}
            {fullImageUrl && !hasRotation && (
              <ImageOverlay url={fullImageUrl} bounds={bounds} opacity={1} zIndex={1} />
            )}

            <MapClickHandler
              mode={mode}
              nodeType={nodeType}
              onAddNode={handleAddNode}
              onSelectNode={handleSelectNode}
            />

            {/* Render Edges */}
            {edges.map((edge) => {
              // Handle both API format (from_node_id/to_node_id) and local format (nodeAId/nodeBId)
              const fromNodeId = edge.from_node_id ?? edge.nodeAId;
              const toNodeId = edge.to_node_id ?? edge.nodeBId;
              const nodeA = nodes.find((n) => n.id === fromNodeId);
              const nodeB = nodes.find((n) => n.id === toNodeId);

              if (!nodeA || !nodeB) return null;

              // Get display positions using pixel-to-GPS conversion
              const posA = getNodeDisplayPosition(nodeA);
              const posB = getNodeDisplayPosition(nodeB);

              if (!posA || !posB) return null;

              return (
                <Polyline
                  key={edge.id}
                  positions={[
                    [posA.lat, posA.lng],
                    [posB.lat, posB.lng],
                  ]}
                  pathOptions={{
                    color: '#607D8B',
                    weight: 2,
                    opacity: 0.7,
                  }}
                  eventHandlers={{
                    click: () => {
                      if (mode === 'delete') {
                        handleDeleteEdge(edge.id);
                      }
                    },
                  }}
                />
              );
            })}

            {/* Render Nodes */}
            {nodes.map((node) => {
              const isSelected = selectedNodes.some((n) => n.id === node.id);
              const nodeTypeValue = node.node_type ?? node.type;

              // Get display position using pixel-to-GPS conversion
              const pos = getNodeDisplayPosition(node);

              if (!pos) {
                console.warn('[WayfindingGraphEditor] Node missing coordinates:', node);
                return null;
              }

              return (
                <CircleMarker
                  key={node.id}
                  center={[pos.lat, pos.lng]}
                  radius={isSelected ? 10 : 8}
                  pathOptions={{
                    color: isSelected ? '#FF9800' : getNodeColor(nodeTypeValue),
                    fillColor: isSelected ? '#FF9800' : getNodeColor(nodeTypeValue),
                    fillOpacity: 0.8,
                    weight: isSelected ? 3 : 2,
                  }}
                  eventHandlers={{
                    click: () => handleSelectNode(node),
                  }}
                />
              );
            })}
          </MapContainer>
        )}
      </Box>

      {/* Link Location Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Link Location to Node
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a location (room, unit, etc.) to link to this wayfinding node.
            This enables showing the exact position on floor plans when alarms occur at this location.
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Search locations..."
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ mb: 2 }}
          />

          {loadingLocations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredLocations.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No locations found"
                    secondary="Try adjusting your search"
                  />
                </ListItem>
              ) : (
                filteredLocations.map((location) => {
                  const isAlreadyLinked = location.wayfinding_node_id && location.wayfinding_node_id !== selectedNodes[0]?.id;
                  return (
                    <ListItemButton
                      key={location.id}
                      onClick={() => handleLinkLocation(location)}
                      disabled={isAlreadyLinked}
                    >
                      <ListItemIcon>
                        <RoomIcon color={isAlreadyLinked ? 'disabled' : 'primary'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={location.friendlyName}
                        secondary={
                          <>
                            {location.locationTypeModel?.friendlyName || 'Location'}
                            {isAlreadyLinked && (
                              <Chip
                                label="Already linked"
                                size="small"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                  );
                })
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Building Entrance Creation Dialog */}
      <Dialog
        open={entranceDialogOpen}
        onClose={handleSkipBuildingEntrance}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EntranceIcon color="success" />
            Create Building Entrance
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You've placed an entrance node. Create a Building Entrance record to enable navigation to this entrance.
          </Typography>

          <TextField
            fullWidth
            label="Entrance Name"
            value={entranceForm.name}
            onChange={(e) => setEntranceForm({ ...entranceForm, name: e.target.value })}
            placeholder="e.g., Main Entrance, Side Door, Emergency Exit"
            sx={{ mb: 2 }}
            autoFocus
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Entrance Type</InputLabel>
            <Select
              value={entranceForm.entrance_type}
              onChange={(e) => setEntranceForm({ ...entranceForm, entrance_type: e.target.value })}
              label="Entrance Type"
            >
              <MenuItem value="main">Main Entrance</MenuItem>
              <MenuItem value="side">Side Entrance</MenuItem>
              <MenuItem value="emergency">Emergency Exit</MenuItem>
              <MenuItem value="accessible">Accessible Entrance</MenuItem>
              <MenuItem value="service">Service Entrance</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={entranceForm.is_default}
                  onChange={(e) => setEntranceForm({ ...entranceForm, is_default: e.target.checked })}
                />
              }
              label="Set as default entrance for this location"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={entranceForm.is_accessible}
                  onChange={(e) => setEntranceForm({ ...entranceForm, is_accessible: e.target.checked })}
                />
              }
              label="Wheelchair accessible"
            />
          </Box>

          {pendingEntranceNode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Node coordinates: {pendingEntranceNode.geo_lat?.toFixed(6)}, {pendingEntranceNode.geo_lng?.toFixed(6)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipBuildingEntrance} color="inherit">
            Skip (Node Only)
          </Button>
          <Button
            onClick={handleCreateBuildingEntrance}
            variant="contained"
            disabled={!entranceForm.name}
          >
            Create Entrance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Entrance Node to Existing Building Entrance Dialog */}
      <Dialog
        open={linkEntranceDialogOpen}
        onClose={() => {
          setLinkEntranceDialogOpen(false);
          setPendingLinkEntranceNode(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Link to Building Entrance
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Link this entrance node to an existing building entrance to enable navigation.
          </Typography>

          {buildingEntrances.length === 0 ? (
            <Alert severity="info">
              No building entrances found. Create one using the entrance node dialog.
            </Alert>
          ) : (
            <List>
              {buildingEntrances.map((entrance) => {
                const isLinked = !!entrance.wayfinding_node_id;
                const isLinkedToThis = entrance.wayfinding_node_id === pendingLinkEntranceNode?.id;

                return (
                  <ListItem key={entrance.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleLinkBuildingEntrance(entrance)}
                      disabled={isLinked && !isLinkedToThis}
                    >
                      <ListItemIcon>
                        <EntranceIcon color={isLinkedToThis ? 'success' : isLinked ? 'disabled' : 'primary'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entrance.name}
                            {isLinkedToThis && (
                              <Chip label="Currently linked" size="small" color="success" />
                            )}
                            {isLinked && !isLinkedToThis && (
                              <Chip label="Linked to another node" size="small" color="warning" />
                            )}
                          </Box>
                        }
                        secondary={`${entrance.entrance_type || 'entrance'}${entrance.is_default ? ' • Default' : ''}`}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLinkEntranceDialogOpen(false);
              setPendingLinkEntranceNode(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Node Dialog */}
      <Dialog
        open={editNodeDialogOpen}
        onClose={() => setEditNodeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Node</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Name your node for easier identification, especially for stairs and elevators that need to be linked across floors.
          </Typography>

          <TextField
            fullWidth
            label="Node Name"
            value={editNodeForm.name}
            onChange={(e) => setEditNodeForm({ ...editNodeForm, name: e.target.value })}
            placeholder="e.g., Main Staircase, Elevator A, West Entrance"
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Latitude (geo_lat)"
            value={editNodeForm.geo_lat}
            onChange={(e) => setEditNodeForm({ ...editNodeForm, geo_lat: e.target.value })}
            type="number"
            inputProps={{ step: '0.000001' }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Longitude (geo_lng)"
            value={editNodeForm.geo_lng}
            onChange={(e) => setEditNodeForm({ ...editNodeForm, geo_lng: e.target.value })}
            type="number"
            inputProps={{ step: '0.000001' }}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <InputLabel>Node Type</InputLabel>
            <Select
              value={editNodeForm.node_type}
              onChange={(e) => setEditNodeForm({ ...editNodeForm, node_type: e.target.value })}
              label="Node Type"
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="stairs">Stairs</MenuItem>
              <MenuItem value="elevator">Elevator</MenuItem>
              <MenuItem value="entrance">Entrance</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNodeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNodeEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Floor Connection Dialog */}
      <Dialog
        open={multiFloorDialogOpen}
        onClose={() => setMultiFloorDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LayersIcon color="secondary" />
            Connect to Another Floor
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect this {selectedNodes[0]?.node_type} node to a node on another floor.
            Both directions will be created automatically (up and down).
          </Typography>

          {/* Edge Type Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Connection Type</InputLabel>
            <Select
              value={multiFloorEdgeType}
              onChange={(e) => setMultiFloorEdgeType(e.target.value)}
              label="Connection Type"
            >
              <MenuItem value="stairs_up">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StairsIcon /> Stairs
                </Box>
              </MenuItem>
              <MenuItem value="elevator_up">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ElevatorIcon /> Elevator
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Target Floor Selection */}
          <Typography variant="subtitle2" gutterBottom>
            1. Select Target Floor
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {otherFloorPlans.length === 0 ? (
              <Alert severity="warning" sx={{ width: '100%' }}>
                No other floor plans found. Upload floor plans for other floors first.
              </Alert>
            ) : (
              otherFloorPlans.map((fp) => (
                <Chip
                  key={fp.id}
                  label={`${fp.floor_name} (Floor ${fp.floor_number})`}
                  onClick={() => loadTargetFloorNodes(fp)}
                  color={selectedTargetFloor?.id === fp.id ? 'primary' : 'default'}
                  variant={selectedTargetFloor?.id === fp.id ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))
            )}
          </Box>

          {/* Target Node Selection */}
          {selectedTargetFloor && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                2. Select Target Node on {selectedTargetFloor.floor_name}
              </Typography>
              {loadingTargetFloor ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : targetFloorNodes.length === 0 ? (
                <Alert severity="warning">
                  No nodes found on this floor. Create nodes on the target floor first.
                </Alert>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {targetFloorNodes
                    .filter(n => n.node_type === 'stairs' || n.node_type === 'elevator')
                    .map((node) => (
                      <ListItemButton
                        key={node.id}
                        selected={selectedTargetNode?.id === node.id}
                        onClick={() => setSelectedTargetNode(node)}
                      >
                        <ListItemIcon>
                          {node.node_type === 'stairs' ? (
                            <StairsIcon color="secondary" />
                          ) : (
                            <ElevatorIcon color="primary" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={node.name || `${node.node_type.charAt(0).toUpperCase() + node.node_type.slice(1)} Node (ID: ${node.id})`}
                          secondary={node.name
                            ? `${node.node_type.charAt(0).toUpperCase() + node.node_type.slice(1)} | ID: ${node.id}`
                            : `Position: ${node.x_position}, ${node.y_position}`}
                        />
                      </ListItemButton>
                    ))}
                  {targetFloorNodes.filter(n => n.node_type === 'stairs' || n.node_type === 'elevator').length === 0 && (
                    <Alert severity="info" sx={{ m: 1 }}>
                      No stairs or elevator nodes on this floor. Only stairs/elevator nodes can be connected across floors.
                    </Alert>
                  )}
                </List>
              )}
            </>
          )}

          {/* Summary */}
          {selectedTargetNode && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>Connection Summary:</strong><br />
              {selectedNodes[0]?.name || `${selectedNodes[0]?.node_type} (ID: ${selectedNodes[0]?.id})`} on {floorPlan?.floor_name}
              {' '}&harr;{' '}
              {selectedTargetNode.name || `${selectedTargetNode.node_type} (ID: ${selectedTargetNode.id})`} on {selectedTargetFloor?.floor_name}<br />
              Type: {multiFloorEdgeType.includes('stairs') ? 'Stairs' : 'Elevator'} (bidirectional)
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMultiFloorDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateMultiFloorEdge}
            variant="contained"
            color="secondary"
            disabled={!selectedTargetNode}
            startIcon={<LayersIcon />}
          >
            Create Connection
          </Button>
        </DialogActions>
      </Dialog>

      {/* GPS Calibration Mode Dialog */}
      <Dialog
        open={calibrationModeOpen}
        onClose={() => setCalibrationModeOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { height: '90vh', maxHeight: 700 } }}
      >
        <NodeCalibrationMode
          buildingData={{
            id: floorPlan?.location_id,
            floors: allFloorsWithNodes.length > 0 ? allFloorsWithNodes : [{ ...floorPlan, nodes }],
          }}
          floorPlanId={floorPlan?.id}
          onCalibrationComplete={(result) => {
            setCalibrationModeOpen(false);
            if (result.success) {
              showSnackbar(`Calibration applied to ${result.nodesUpdated} nodes (avg error: ${result.calibrationError?.toFixed(1) || '?'}m)`, 'success');
              loadGraph(); // Reload to get updated coordinates
            }
          }}
          onClose={() => setCalibrationModeOpen(false)}
        />
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WayfindingGraphEditor;
